/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from "react";
import { useQuery, UseQueryResult } from "react-query";
import { TCTeamsFxContext } from "../components/TCTeamsContext";
import {
  getResponseUsingResponseType,
  handleTcTeamsAPIError,
} from "../services/utility";
import { ApiRequest } from "../types/ApiRequest";
import { TeamsTeamcenterLogger } from "teamsteamcenterobservability";
import { ErrorMessage } from "../types/CommonTypes";
import { Configuration } from "../services/configuration";
import { Constants } from "../common/constants";
import { TeamsFxContext } from "../components/Context";

/**
 * allows to call any APIS from the backend, with the authentication bearer token.
 *
 * @param request Api request object
 * @return {UseQueryResult} query result
 */
export const useApi = (
  request: ApiRequest,
  logger: TeamsTeamcenterLogger
): UseQueryResult<any, any> => {
  const tcTeamsContext = useContext(TCTeamsFxContext);
  const teamsContext = useContext(TeamsFxContext);
  let callKey = request.key ? request.key : request.url;

  callKey = callKey instanceof Array ? callKey : [callKey, request.body];

  // calling API
  const queryResult: UseQueryResult<any, Error> = useQuery<any, Error>(
    callKey,
    () => callApiAsync(request, logger, teamsContext, tcTeamsContext),
    {
      refetchInterval: 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: request.enabled,
      retry: false,
      useErrorBoundary: false,
      onSuccess: () => {
        if (request.invalidateQuery) {
          tcTeamsContext?.queryClient?.invalidateQueries({
            predicate: (query: any) => {
              for (const key of query.queryKey as string[]) {
                const invalidate =
                  (request.invalidateQuery && request.invalidateQuery(key)) ||
                  false;
                if (invalidate) return true;
              }
              return false;
            },
          });
        }
      },
    }
  );

  return { ...queryResult };
};

export const callApiAsync = async (
  request: ApiRequest,
  logger: TeamsTeamcenterLogger,
  teamsContext: any,
  tcTeamsContext: any
) => {
  const accessToken = await teamsContext?.teamsUserCredential?.getToken("");
  if (!accessToken || !request.url || !request.enabled) {
    return null;
  }

  // Get TenantId for logger
  if (
    typeof teamsContext?.userInfo !== "undefined" &&
    teamsContext?.userInfo !== null
  ) {
    const tenantId = teamsContext?.userInfo?.tenantId;
    if (tenantId !== null) {
      logger.setTenantId(tenantId);
    }
  }

  const logContext = logger.createContext({ actionId: request.action });
  if (request.action) {
    logger.logInformation(request.action + "() called.", logContext);
  }

  const correlationId = logContext.requestId;
  const teamsLang = Configuration.getValueFromLocalStorage(
    Constants.cacheTeamcenterLanguage
  );
  const teamcenterConfiguration = Configuration.getValueFromLocalStorage(
    Constants.cacheTeamcenterCode
  );
  const selectedSite = Configuration.getValueFromLocalStorage(
    Constants.cacheSelectedSite
  );
  const activeCapability = Configuration.getValueFromLocalStorage(
    Constants.activeCapability
  );
  const configuration = Configuration.getValueFromLocalStorage(
    Constants.cacheConfiguration
  );
  const tcVersionParam = localStorage.getItem(Constants.cacheTcVersion);
  // preparing the headers
  const headers = new Headers();
  headers.append("Authorization", `Bearer ${accessToken.token}`);
  headers.append("Content-Type", "application/json");
  headers.append("Accept", "application/json");
  headers.append(
    "sessionId",
    tcTeamsContext?.teamcenter.session?.sessionId || ""
  );
  headers.append("userId", tcTeamsContext?.teamcenter.session?.userId || "");
  headers.append("x-siemens-session-id", logContext.sessionId);
  headers.append("x-siemens-operation-id", logContext.actionId);
  headers.append("appType", Constants.appType);
  headers.append("userAadOid", teamsContext?.userInfo?.objectId || "");
  headers.append("x-correlation-id", correlationId);
  headers.append("locale", teamsLang);
  if (activeCapability && activeCapability.capabilityName) {
    headers.append("capability", activeCapability.capabilityName);
  }
  if (configuration && configuration.tcVersion) {
    headers.append("tcVersion", configuration.tcVersion);
  } else if (tcVersionParam) {
    headers.append("tcVersion", tcVersionParam);
  } else {
    headers.append("tcVersion", "default");
  }
  if (teamcenterConfiguration && teamcenterConfiguration.siteId) {
    headers.append("siteId", teamcenterConfiguration.siteId);
  } else {
    if (selectedSite && selectedSite.siteId) {
      headers.append("siteId", selectedSite.siteId);
    }
  }

  // Check if appDeploymentId is already provided in request headers
  // In case of api calls to MUN broker, app deployment id is provided already in request headers
  const hasAppDeploymentId =
    request.headers?.some(
      ([key, value]) =>
        key.toLowerCase() === "appdeploymentid" &&
        value !== null &&
        value !== undefined &&
        value !== ""
    ) ?? false;

  if (selectedSite && selectedSite.appDeploymentId && !hasAppDeploymentId) {
    headers.append("appDeploymentId", selectedSite.appDeploymentId);
  }

  if (request.headers) {
    request.headers.forEach((header) => {
      headers.append(header[0], header[1]);
    });
  }
  const requestInit: RequestInit = {
    method: request.method ?? "GET",
    headers: headers,
  };

  if (
    request.method &&
    (request.method === "POST" || request.method === "PUT")
  ) {
    requestInit.body = request.body;
  }

  const response = await fetch(request.url, requestInit);

  if (request.action && response) {
    logger.logInformation(request.action + "() completed.", logContext);
  }

  if (!response || response.status < 200 || response.status > 204) {
    const errorMessage: ErrorMessage = await handleTcTeamsAPIError(
      request.url,
      correlationId,
      response,
      logContext
    );
    if (response.status === 504)
      errorMessage.message = "The request timed out. Please try again.";
    tcTeamsContext?.failure.setError(errorMessage);
  }

  return await getResponseUsingResponseType(request.returnType, response);
};
